import { useId, useState } from "react";
import { DESCRICAO_PAPEL, ROTULO_PAPEL, USER_ROLES, type UserRole } from "@foto/shared";
import { trpc, type SaidaRota } from "../../lib/trpc.js";
import { Pagina, Lista } from "../../components/Pagina.js";
import {
  Botao,
  Campo,
  Chave,
  Entrada,
  Falha,
  Girando,
  Selecao,
  Selo,
  Vazio,
} from "../../components/ui.js";
import { useAuth } from "../auth/useAuth.js";

type Usuario = SaidaRota["users"]["list"][number];

export function UsuariosPage() {
  const { usuario: eu } = useAuth();
  const lista = trpc.users.list.useQuery();
  const utils = trpc.useUtils();

  const [criando, setCriando] = useState(false);
  const [editando, setEditando] = useState<Usuario | null>(null);

  const itens = lista.data ?? [];

  return (
    <Pagina
      titulo="Usuários"
      descricao="Quem entra no sistema e até onde vai."
      acao={
        !criando && (
          <Botao tom="brasa" onClick={() => setCriando(true)}>
            Novo usuário
          </Botao>
        )
      }
    >
      {criando && (
        <Formulario
          aoFechar={() => setCriando(false)}
          aoSalvar={() => {
            void utils.users.list.invalidate();
            setCriando(false);
          }}
        />
      )}

      {editando && (
        <Formulario
          usuario={editando}
          aoFechar={() => setEditando(null)}
          aoSalvar={() => {
            void utils.users.list.invalidate();
            setEditando(null);
          }}
        />
      )}

      {lista.isLoading ? (
        <Girando className="mx-auto my-12 size-6 text-texto-fraco" />
      ) : lista.isError ? (
        <Falha
          mensagem={`Não foi possível carregar os usuários. ${lista.error.message}`}
          tentarNovamente={() => void lista.refetch()}
        />
      ) : itens.length === 0 ? (
        <Vazio
          titulo="Nenhum usuário"
          descricao="Cadastre a primeira pessoa que vai usar o sistema."
          acao={
            <Botao tom="brasa" onClick={() => setCriando(true)}>
              Novo usuário
            </Botao>
          }
        />
      ) : (
        <Lista
          itens={itens}
          chave={(u) => u.id}
          colunas={["Nome", "E-mail", "Acesso", "Trabalha para", ""]}
          cartao={(u) => (
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-[0.9375rem] text-texto">{u.name}</p>
                <p className="font-mono text-[0.8125rem] text-texto-suave">{u.email}</p>
                <div className="mt-1.5 flex flex-wrap gap-1.5">
                  <Selo tom={u.role === "admin" ? "brasa" : "quieto"}>{ROTULO_PAPEL[u.role]}</Selo>
                  <SeloDoWhatsapp usuario={u} />
                </div>
                {u.ownerUserId !== null && (
                  <p className="mt-1 text-[0.75rem] text-texto-fraco">{donoDe(u, itens)}</p>
                )}
                <CodigoDoQuiosque usuario={u} />
              </div>
              <Acoes usuario={u} eu={eu?.id} aoEditar={() => setEditando(u)} />
            </div>
          )}
          linha={(u) => [
            <span key="n">{u.name}</span>,
            <span key="e" className="font-mono text-[0.8125rem] text-texto-suave">
              {u.email}
            </span>,
            <span key="p" className="flex flex-wrap gap-1.5">
              <Selo tom={u.role === "admin" ? "brasa" : "quieto"}>{ROTULO_PAPEL[u.role]}</Selo>
              <SeloDoWhatsapp usuario={u} />
            </span>,
            <span key="d" className="text-texto-suave">
              {u.ownerUserId === null ? "—" : donoDe(u, itens)}
              <CodigoDoQuiosque usuario={u} />
            </span>,
            <Acoes key="a" usuario={u} eu={eu?.id} aoEditar={() => setEditando(u)} />,
          ]}
        />
      )}
    </Pagina>
  );
}

/**
 * O nome do cliente a quem a conta pertence.
 *
 * A lista já traz todos os usuários, então o nome sai daqui mesmo em vez de
 * outra consulta. Cliente apagado (`set null` na FK) cai no travessão.
 */
function donoDe(u: Usuario, todos: Usuario[]): string {
  return todos.find((o) => o.id === u.ownerUserId)?.name ?? "—";
}

/** A conta de cliente liberada para enviar pelo WhatsApp da SER (#105). */
function SeloDoWhatsapp({ usuario }: { usuario: Usuario }) {
  if (usuario.role !== "cliente" || !usuario.whatsappLiberado) return null;
  return <Selo tom="fixado">WhatsApp</Selo>;
}

/**
 * O código que a TV digita.
 *
 * Aqui e não só na tela Kiosk: um quiosque criado nesta página não é o que a
 * tela Kiosk do cliente mostra, e sem isto o código dele não aparecia em lugar
 * nenhum.
 */
function CodigoDoQuiosque({ usuario }: { usuario: Usuario }) {
  if (usuario.role !== "kiosk" || !usuario.kioskCode) return null;

  return (
    <span className="mt-1 block font-mono text-[0.75rem] tracking-[0.15em] text-texto-suave">
      Código {usuario.kioskCode}
    </span>
  );
}

function Acoes({
  usuario,
  eu,
  aoEditar,
}: {
  usuario: Usuario;
  eu: number | undefined;
  aoEditar: () => void;
}) {
  const utils = trpc.useUtils();
  const [confirmando, setConfirmando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const remover = trpc.users.delete.useMutation({
    onSuccess: () => {
      setConfirmando(false);
      void utils.users.list.invalidate();
    },
    onError: (e) => {
      setConfirmando(false);
      setErro(e.message);
    },
  });

  // O backend também recusa, mas esconder o botão evita oferecer o que não vai
  // funcionar — e "excluir a si mesmo" é um clique fácil de dar sem pensar.
  const souEu = usuario.id === eu;

  return (
    <div className="flex flex-col items-end gap-2">
      <div className="flex gap-2">
        <Botao tom="fantasma" onClick={aoEditar}>
          Editar
        </Botao>
        {!souEu && !confirmando && (
          <Botao tom="fantasma" onClick={() => setConfirmando(true)}>
            Excluir
          </Botao>
        )}
      </div>

      {confirmando && (
        <div className="flex items-center gap-2">
          <span className="text-[0.8125rem] text-texto-suave">Excluir {usuario.name}?</span>
          <Botao tom="quieto" onClick={() => setConfirmando(false)}>
            Não
          </Botao>
          <Botao
            tom="descartar"
            onClick={() => remover.mutate({ id: usuario.id })}
            carregando={remover.isPending}
          >
            Excluir
          </Botao>
        </div>
      )}

      {erro && <span className="text-[0.8125rem] text-descartar">{erro}</span>}
    </div>
  );
}

function Formulario({
  usuario,
  aoFechar,
  aoSalvar,
}: {
  usuario?: Usuario;
  aoFechar: () => void;
  aoSalvar: () => void;
}) {
  const [nome, setNome] = useState(usuario?.name ?? "");
  const [email, setEmail] = useState(usuario?.email ?? "");
  const [senha, setSenha] = useState("");
  const [papel, setPapel] = useState<UserRole>(usuario?.role ?? "operador");
  const [dono, setDono] = useState<string>(usuario?.ownerUserId ? String(usuario.ownerUserId) : "");
  const [whatsapp, setWhatsapp] = useState(usuario?.whatsappLiberado ?? false);
  const [erro, setErro] = useState<string | null>(null);
  const idDaDicaDoWhatsapp = useId();

  /**
   * Operador e quiosque trabalham para um cliente.
   *
   * A lista de donos possíveis são as contas de cliente — as únicas que têm
   * acervo. O backend recusa qualquer outra, e recusa também a conta sem dono:
   * ela enxergaria um acervo vazio, e o defeito só apareceria no evento.
   */
  const precisaDeDono = papel === "operador" || papel === "kiosk";
  const usuarios = trpc.users.list.useQuery(undefined, { enabled: precisaDeDono });
  const clientes = (usuarios.data ?? []).filter((u) => u.role === "cliente");

  const criar = trpc.users.create.useMutation({
    onSuccess: aoSalvar,
    onError: (e) => setErro(e.message),
  });
  const editar = trpc.users.update.useMutation({
    onSuccess: aoSalvar,
    onError: (e) => setErro(e.message),
  });

  const salvando = criar.isPending || editar.isPending;

  // Na edição a senha em branco significa "não alterar" — a mesma semântica dos
  // segredos nas configurações.
  const valido =
    nome.trim().length >= 2 &&
    email.includes("@") &&
    (usuario ? true : senha.length >= 8) &&
    (!precisaDeDono || dono !== "");

  function salvar() {
    setErro(null);
    const base = {
      name: nome.trim(),
      email: email.trim().toLowerCase(),
      role: papel,
      ownerUserId: precisaDeDono ? Number(dono) : null,
    };

    if (usuario) {
      // A liberação só vai quando muda, e só na conta de cliente: é onde ela vale.
      const liberacao =
        papel === "cliente" && whatsapp !== usuario.whatsappLiberado
          ? { whatsappLiberado: whatsapp }
          : {};
      editar.mutate({
        id: usuario.id,
        ...base,
        ...(senha ? { password: senha } : {}),
        ...liberacao,
      });
    } else {
      criar.mutate({ ...base, password: senha });
    }
  }

  return (
    <div className="mb-6 flex flex-col gap-4 rounded-peca border border-borda bg-superficie p-4">
      <Campo rotulo="Nome">
        <Entrada value={nome} onChange={(e) => setNome(e.target.value)} autoFocus />
      </Campo>

      <Campo rotulo="E-mail" dica="É com ele que a pessoa entra no sistema.">
        <Entrada
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoCapitalize="none"
          autoCorrect="off"
        />
      </Campo>

      <Campo
        rotulo="Senha"
        dica={usuario ? "Deixe em branco para manter a atual." : "Ao menos 8 caracteres."}
      >
        <Entrada
          type="password"
          value={senha}
          onChange={(e) => setSenha(e.target.value)}
          autoComplete="new-password"
        />
      </Campo>

      <Campo rotulo="Acesso" dica={DESCRICAO_PAPEL[papel]}>
        <Selecao value={papel} onChange={(e) => setPapel(e.target.value as UserRole)}>
          {USER_ROLES.map((r) => (
            <option key={r} value={r}>
              {ROTULO_PAPEL[r]}
            </option>
          ))}
        </Selecao>
      </Campo>

      {precisaDeDono && (
        <Campo
          rotulo="Trabalha para"
          dica={
            clientes.length === 0
              ? "Nenhum cliente cadastrado ainda: crie a conta do cliente antes desta."
              : "O acervo que esta conta enxerga é o deste cliente."
          }
        >
          <Selecao value={dono} onChange={(e) => setDono(e.target.value)}>
            <option value="">Selecione o cliente</option>
            {clientes.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Selecao>
        </Campo>
      )}

      {/* Só na edição: a conta de cliente nasce sem WhatsApp, e liberar é uma decisão à parte. */}
      {usuario && papel === "cliente" && (
        <div className="flex flex-col gap-1">
          <Chave
            ligada={whatsapp}
            aoAlternar={setWhatsapp}
            rotulo="Pode enviar pelo WhatsApp da SER"
            texto="Pode enviar pelo WhatsApp da SER"
            descricaoId={idDaDicaDoWhatsapp}
          />
          <p id={idDaDicaDoWhatsapp} className="text-[0.8125rem] text-texto-fraco">
            As fotos desta casa, e de quem trabalha para ela, saem pelo número da SER, que é um só e
            compartilhado com a loja e o atendimento. Libere só quem vai usar.
          </p>
        </div>
      )}

      {erro && <Falha mensagem={erro} />}

      <div className="flex gap-2">
        <Botao tom="brasa" onClick={salvar} disabled={!valido} carregando={salvando}>
          {usuario ? "Salvar" : "Criar"}
        </Botao>
        <Botao tom="quieto" onClick={aoFechar} disabled={salvando}>
          Cancelar
        </Botao>
      </div>
    </div>
  );
}
